import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { CatalogFacets, FacetValue } from '@xshop/shared';
import { goToListing, toggled, type ListedFacet, type ListingAddress } from './listingAddress';
import type { ListingWords } from './listingWords';
import { ListingPriceFilter } from './ListingPriceFilter';

// The folds of the filters, drawn the same in the sidebar and in a phone's drawer: availability, price,
// one per option, product type and brand. Each value carries how many products in the listing have it,
// as Sleek's do, and ticking one puts it in the address straight away.

/** Which folds the merchant chose to show, and whether they start open. */
export type FilterChoices = { availability: boolean; price: boolean; options: boolean; types: boolean; vendors: boolean; open: boolean };

/** What the folds are drawn from; floating folds are a row of dropdowns rather than a column. */
type GroupsProps = { facets: CatalogFacets; address: ListingAddress; words: ListingWords; unit: string; show: FilterChoices; floating?: boolean };

// Closes an open dropdown fold on a press outside it or on Escape, as a menu does.
function useOutsideClose(active: boolean, root: React.RefObject<HTMLDivElement | null>, close: () => void): void {
  useEffect(() => {
    if (!active) return undefined;
    // Closes on a press anywhere outside the fold.
    const press = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) close(); };
    // Closes on Escape.
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    document.addEventListener('pointerdown', press);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', press); document.removeEventListener('keydown', key); };
  }, [active, root, close]);
}

/** One fold's name, whether it starts open, whether it floats as a dropdown, and what it holds. */
type FoldProps = { title: string; startOpen: boolean; floating: boolean; children: React.ReactNode };

// Draws one fold: its name as a button that opens and closes it, and what it holds; a floating one starts shut.
const FilterFold: React.FC<FoldProps> = ({ title, startOpen, floating, children }) => {
  const [open, setOpen] = useState(startOpen && !floating);
  const panel = useId();
  const root = useRef<HTMLDivElement>(null);
  useOutsideClose(floating && open, root, useCallback(() => setOpen(false), []));
  return (
    <div ref={root} className="xe-filter">
      <h3 className="xe-filter-head">
        <button type="button" aria-expanded={open} aria-controls={panel} onClick={() => setOpen(!open)}>
          <span dir="auto">{title}</span><span className="xe-filter-sign" data-open={open || undefined} aria-hidden="true" />
        </button>
      </h3>
      <div id={panel} className="xe-filter-body" hidden={!open}>{children}</div>
    </div>
  );
};

// Draws one checkbox row: the words, how many, and whether it is ticked.
const CheckRow: React.FC<{ label: string; count: number; checked: boolean; onToggle: () => void }> = ({ label, count, checked, onToggle }) => (
  <li>
    <label className="xe-filter-check">
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span dir="auto">{label}</span> <span className="xe-filter-count">({count})</span>
    </label>
  </li>
);

// Draws one facet's values, each ticking or unticking itself in the address.
const FacetChecks: React.FC<{ facet: ListedFacet; values: FacetValue[]; address: ListingAddress }> = ({ facet, values, address }) => (
  <ul className="xe-filter-list">
    {values.map((row) => (
      <CheckRow key={row.value} label={row.value} count={row.count} checked={address[facet].includes(row.value)} onToggle={() => goToListing(toggled(address, facet, row.value))} />
    ))}
  </ul>
);

// Draws the folds the merchant chose, leaving out any the listing has nothing to offer for.
export const ListingFilterGroups: React.FC<GroupsProps> = ({ facets, address, words, unit, show, floating = false }) => (
  <>
    {show.availability && facets.inStock > 0 ? (
      <FilterFold floating={floating} startOpen={show.open} title={words.availability}>
        <ul className="xe-filter-list">
          <CheckRow label={words.inStock} count={facets.inStock} checked={address.available} onToggle={() => goToListing({ ...address, available: !address.available, page: 1 })} />
        </ul>
      </FilterFold>
    ) : null}
    {show.price && facets.price.max > facets.price.min ? (
      <FilterFold floating={floating} startOpen={show.open} title={words.price}><ListingPriceFilter span={facets.price} address={address} words={words} unit={unit} /></FilterFold>
    ) : null}
    {show.options ? facets.options.map((option) => (
      <FilterFold key={option.name} floating={floating} startOpen={show.open} title={option.name}><FacetChecks facet="option" values={option.values} address={address} /></FilterFold>
    )) : null}
    {show.types && facets.types.length > 0 ? <FilterFold floating={floating} startOpen={show.open} title={words.type}><FacetChecks facet="type" values={facets.types} address={address} /></FilterFold> : null}
    {show.vendors && facets.vendors.length > 0 ? <FilterFold floating={floating} startOpen={show.open} title={words.vendor}><FacetChecks facet="vendor" values={facets.vendors} address={address} /></FilterFold> : null}
  </>
);
